/**
 * Google Analytics initialization and tracking utilities
 */

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

/**
 * Initialize Google Analytics by injecting the gtag script
 * Should be called once when the app initializes
 */
export function initializeGA(): void {
  if (!GA_MEASUREMENT_ID) {
    console.warn('[Analytics] GA_MEASUREMENT_ID not configured, skipping initialization');
    return;
  }

  // Prevent double-initialization
  if (window.gtag) {
    console.warn('[Analytics] Google Analytics already initialized');
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    // Must use `arguments` (not rest params) — gtag.js expects Arguments objects in dataLayer
    window.dataLayer.push(arguments);
  };

  // Configure gtag
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // We'll manually send page views for SPA routing
  });

  // Inject gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  console.log('[Analytics] Google Analytics initialized:', GA_MEASUREMENT_ID);
}

/**
 * Track a page view
 * @param path - The page path (e.g., '/dashboard', '/leaderboard')
 * @param title - Optional page title
 */
export function trackPageView(path: string, title?: string): void {
  if (!GA_MEASUREMENT_ID || !window.gtag) {
    return;
  }

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  });

  console.log('[Analytics] Page view tracked:', path);
}

/**
 * Track a custom event
 * @param eventName - Name of the event
 * @param eventParams - Optional event parameters
 */
export function trackEvent(eventName: string, eventParams?: Record<string, any>): void {
  if (!GA_MEASUREMENT_ID || !window.gtag) {
    return;
  }

  window.gtag('event', eventName, eventParams);
  console.log('[Analytics] Event tracked:', eventName, eventParams);
}

/**
 * Set a user property for Google Analytics
 * @param properties - Key-value pairs of user properties
 */
export function setUserProperties(properties: Record<string, any>): void {
  if (!GA_MEASUREMENT_ID || !window.gtag) {
    return;
  }

  window.gtag('set', 'user_properties', properties);
}
